import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowUpRight, Calendar, Loader2, Plus } from "lucide-react";
import { seededEvents } from "@/data/seed-imam-ali-2026";
import { getEnabledModules, moduleRegistry } from "@/components/modules/registry";
import { api } from "@/lib/api-client";
import type { Event } from "@/lib/event-schema";

type LoadState =
  | { status: "loading" }
  | { status: "ready"; events: Event[] }
  | { status: "error"; message: string; fallback: Event[] };

export function AdminEventsList() {
  const [state, setState] = useState<LoadState>({ status: "loading" });

  useEffect(() => {
    let cancelled = false;
    api.events
      .list()
      .then(async (summaries) => {
        const events = await Promise.all(
          summaries.map((s) =>
            api.events.get(s.slug).catch(() => seededEvents[s.slug] ?? null),
          ),
        );
        if (cancelled) return;
        const filtered = events.filter((e): e is Event => Boolean(e));
        setState({ status: "ready", events: filtered });
      })
      .catch((err) => {
        if (cancelled) return;
        setState({
          status: "error",
          message: err instanceof Error ? err.message : "Failed to load events.",
          fallback: Object.values(seededEvents),
        });
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const events =
    state.status === "ready"
      ? state.events
      : state.status === "error"
        ? state.fallback
        : [];

  return (
    <div className="mx-auto w-full max-w-5xl">
      <header className="flex items-end justify-between gap-4 flex-wrap mb-8">
        <div>
          <p className="eyebrow text-accent">Admin</p>
          <h1 className="display-title mt-2 text-[clamp(2rem,4vw,3rem)] leading-tight">Events</h1>
          <p className="mt-2 text-sm text-muted">
            Create a new event or edit an existing one. Toggling a module hides it
            from the public page; submissions are preserved.
          </p>
        </div>
        <Link
          to="/admin/events/new"
          className="inline-flex items-center gap-2 rounded-full bg-ink px-5 py-3 text-sm font-medium text-canvas dark:bg-accent dark:text-accent-on hover:bg-ink/90 dark:hover:bg-accent/90 shadow-glow transition-colors focus-ring"
        >
          <Plus className="h-4 w-4" strokeWidth={1.75} />
          New event
        </Link>
      </header>

      {state.status === "loading" ? (
        <div className="grid h-32 place-items-center text-muted">
          <Loader2 className="h-5 w-5 animate-spin" strokeWidth={1.75} />
        </div>
      ) : null}

      {state.status === "error" ? (
        <p className="mb-4 text-xs text-red-600 dark:text-red-400">
          Couldn't reach the API; showing local seed. ({state.message})
        </p>
      ) : null}

      <div className="grid gap-4">
        {events.map((event) => {
          const enabled = getEnabledModules(event);
          return (
            <article
              key={event.slug}
              className="surface-card flex flex-col gap-5 p-6 lg:flex-row lg:items-center lg:justify-between"
            >
              <div className="flex-1">
                <div className="flex flex-wrap items-center gap-3">
                  <h2 className="font-display text-xl font-light text-ink">
                    {event.title}
                  </h2>
                  <span
                    className={
                      event.status === "published"
                        ? "rounded-full bg-accent-soft px-2.5 py-0.5 text-[11px] uppercase tracking-[0.14em] text-accent-ink dark:text-accent"
                        : "rounded-full border border-line/70 px-2.5 py-0.5 text-[11px] uppercase tracking-[0.14em] text-muted"
                    }
                  >
                    {event.status}
                  </span>
                </div>
                {event.modules.hero?.date ? (
                  <p className="mt-1 inline-flex items-center gap-1.5 text-xs text-muted">
                    <Calendar className="h-3.5 w-3.5" strokeWidth={1.75} />
                    {event.modules.hero.date}
                  </p>
                ) : null}
                <div className="mt-4 flex flex-wrap gap-1.5">
                  {enabled.map((m) => (
                    <span
                      key={m.id}
                      className="rounded-full border border-line/70 bg-surface px-2.5 py-1 text-[11px] uppercase tracking-[0.12em] text-muted"
                    >
                      {moduleRegistry[m.id].label}
                    </span>
                  ))}
                </div>
              </div>
              <div className="flex items-center gap-2">
                <Link
                  to={`/${event.slug}`}
                  className="inline-flex items-center gap-1.5 rounded-full border border-line/70 px-4 py-2 text-sm text-ink hover:border-accent hover:text-accent transition-colors"
                >
                  View
                  <ArrowUpRight className="h-3.5 w-3.5" strokeWidth={1.75} />
                </Link>
                <Link
                  to={`/admin/events/${event.slug}/dashboard`}
                  className="inline-flex items-center gap-1.5 rounded-full border border-line/70 px-4 py-2 text-sm text-ink hover:border-accent hover:text-accent transition-colors"
                >
                  Dashboard
                </Link>
                <Link
                  to={`/admin/events/${event.slug}`}
                  className="inline-flex items-center gap-1.5 rounded-full bg-ink px-4 py-2 text-sm font-medium text-canvas dark:bg-accent dark:text-accent-on hover:bg-ink/90 dark:hover:bg-accent/90"
                >
                  Edit
                </Link>
              </div>
            </article>
          );
        })}
      </div>
    </div>
  );
}
